import PropTypes from "prop-types"
import React, { useCallback } from "react"

import ActivityEntry from "./ActivityEntry"
import AddNotePopover from "./AddNotePopover"
import EmptyState from "./EmptyState"
import ErrorState from "./ErrorState"
import LoadingState from "./LoadingState"

const SCROLL_THRESHOLD_PX = 200

const JobActivityFeed = ({
  entries,
  isLoading = false,
  isError = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  fetchNextPage = () => {},
  onAddNote,
  isAddingNote = false,
}) => {
  const handleScroll = useCallback(
    (event) => {
      if (!hasNextPage || isFetchingNextPage) return
      const { scrollHeight, scrollTop, clientHeight } = event.target
      if (scrollHeight - scrollTop - SCROLL_THRESHOLD_PX <= clientHeight) {
        fetchNextPage()
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage]
  )

  const renderBody = () => {
    if (isError) return <ErrorState />
    if (isLoading) return <LoadingState />
    if (entries.length === 0) return <EmptyState />

    return (
      <>
        <ul className="flex list-none flex-col gap-3 pl-0">
          {entries.map((entry) => (
            <li key={entry.id}>
              <ActivityEntry entry={entry} />
            </li>
          ))}
        </ul>
        {isFetchingNextPage && (
          <div
            aria-busy="true"
            className="py-3 text-center text-xs text-gray-500"
          >
            Loading more…
          </div>
        )}
      </>
    )
  }

  return (
    <section aria-label="Activity" className="flex h-full flex-col">
      <div className="border-b border-gray-200 bg-white px-4">
        <div className="flex items-center justify-between">
          <h3 className="mb-0 text-xs font-semibold uppercase tracking-wide text-gray-500 xl:my-0">
            Activity
          </h3>
          {onAddNote && (
            <AddNotePopover
              onSubmit={onAddNote}
              disabled={isAddingNote}
              submitting={isAddingNote}
            />
          )}
        </div>
        <p className="mt-0.5 text-xs text-gray-400">
          Internal to your team — customers don&apos;t see this.
        </p>
      </div>
      <div
        className="max-h-[420px] overflow-y-auto px-4 py-3"
        onScroll={handleScroll}
      >
        {renderBody()}
      </div>
    </section>
  )
}

JobActivityFeed.propTypes = {
  entries: PropTypes.arrayOf(ActivityEntry.entryShape).isRequired,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  hasNextPage: PropTypes.bool,
  isFetchingNextPage: PropTypes.bool,
  fetchNextPage: PropTypes.func,
  onAddNote: PropTypes.func,
  isAddingNote: PropTypes.bool,
}

export default JobActivityFeed
